import { useEffect, useState } from "react";

import { PatientKind } from "common/types";
import { toCount } from "common/utils/formatting";

import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import Accordion from "client/common/components/interactive/Accordion";

import { ProductLineSearchFilters, SelectedCategoryProductLineFilters } from "../types";
import SearchFilter from "./SearchFilter";

type CategoryProductLineSearchFilterModalProps = {
  options: ProductLineSearchFilters;
  isVisible: boolean;
  onClose: () => void;
  value: SelectedCategoryProductLineFilters;
  onChange: (value: SelectedCategoryProductLineFilters) => void;
};

const CategoryProductLineSearchFilterModal = ({
  options,
  isVisible,
  onClose,
  value,
  onChange,
}: CategoryProductLineSearchFilterModalProps) => {
  const [updatedFilters, setUpdatedFilters] = useState(value);

  useEffect(() => {
    if (isVisible) {
      setUpdatedFilters(value);
    }
    // We want to ensure the params are up to date when the modal first comes into view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const resetFilters = () => {
    setUpdatedFilters({
      manufacturer_ids: [],
      species: [],
      prescription_required: [],
    });
  };

  const applyFilters = () => {
    onClose();
    onChange({ ...updatedFilters });
  };

  const accordionItems = [
    {
      key: "species",
      label: "Species",
      params: updatedFilters.species,
      content: (
        <SearchFilter<PatientKind>
          options={options.species}
          value={updatedFilters.species}
          onChange={(species) =>
            setUpdatedFilters({
              ...updatedFilters,
              species,
            })
          }
        />
      ),
    },
    {
      key: "manufacturer",
      label: "Manufacturers",
      params: updatedFilters.manufacturer_ids,
      content: (
        <SearchFilter
          options={options.manufacturers}
          value={updatedFilters.manufacturer_ids}
          onChange={(manufacturer_ids) =>
            setUpdatedFilters({ ...updatedFilters, manufacturer_ids })
          }
        />
      ),
    },
    {
      key: "prescription_required",
      label: "Type",
      params: updatedFilters.prescription_required,
      content: (
        <SearchFilter
          options={options.prescription_required}
          value={updatedFilters.prescription_required}
          onChange={(prescription_required) =>
            setUpdatedFilters({ ...updatedFilters, prescription_required })
          }
        />
      ),
    },
  ];

  const filterCount = accordionItems.flatMap<number | string | boolean>(
    (item) => item.params,
  ).length;

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Apply Filters" onClose={onClose} />
        <ModalLayout.Content>
          <Accordion.Multi
            items={accordionItems}
            initialOpenKeys={accordionItems.map((item) => item.key)}
          />
        </ModalLayout.Content>
        <ModalLayout.Actions>
          <ModalLayout.Actions.Button
            variant="secondary"
            onClick={resetFilters}
            disabled={!filterCount}
          >
            Reset {toCount(filterCount, "Filter")}
          </ModalLayout.Actions.Button>
          <ModalLayout.Actions.Button onClick={applyFilters}>Apply</ModalLayout.Actions.Button>
        </ModalLayout.Actions>
      </ModalLayout>
    </Modal>
  );
};

export default CategoryProductLineSearchFilterModal;
